/*
 * Presentation Templates — fetch lifecycle + two-level browse state (grid of
 * presentations vs. one presentation's slide grid) + flat selection state.
 *
 * Mirrors the source/commitSelection parameterisation pattern used elsewhere
 * in Deckorate for the same reason: the panel is mounted both in-pane
 * (fetches/inserts directly) and inside the Office Dialog window (mirrors a
 * load state the pane owns, commits via a messageParent round-trip) — see
 * asset-library/hooks/useDialogPresentationTemplates.ts. This hook and its
 * services are entirely independent of the slide-templates feature — no
 * imports from it.
 *
 * Selection stays flat across every open presentation's slides: a slide's
 * `id` is already globally unique per the manifest, so there's no need to
 * scope selection to `openPresentationId`.
 *
 * Every presentation's REAL, full slide list already arrives from
 * fetchPresentationTemplates itself (it eagerly resolves each presentation's
 * per-file `.cache` sibling in parallel — see
 * services/presentationTemplatesApi.ts's `resolveFullSlideLists`), so opening
 * a presentation here is just a lookup, not a fetch.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { getErrorMessage, isAbortError } from "../../shared/services/connectorErrors";
import type { ExtractedSlideImage } from "../../shared/services/pptxSlideImageExtraction";
import {
  fetchPresentationTemplates,
  logPresentationTemplatesError,
  type PresentationTemplate,
  type PresentationTemplateSlide,
} from "../services/presentationTemplatesApi";
import {
  PRESENTATION_INSERT_NOTICE_COPY,
  downloadPresentationPack,
  PresentationDeckReadOnlyError,
  classifyInsertFailure,
  insertPresentationTemplates,
  isPresentationDeckReadOnly,
  presentationSlidesAddedMessage,
  sourcePresentationNames,
} from "../services/presentationTemplateInsert";

const FETCH_FAILED_MESSAGE = "Couldn't load presentation templates";
const INSERT_FAILED_MESSAGE = "Inserting the selected slide(s) failed.";
const NO_SLIDE_DATA_MESSAGE = "These slides have no insertable content yet.";
const DOWNLOAD_FAILED_MESSAGE = "Downloading the presentation failed.";

export type PresentationTemplatesLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; presentations: readonly PresentationTemplate[] };

export type PresentationTemplatesSource =
  /** Fetch here. Only valid where the request can be authorised — the pane. */
  | { readonly kind: "api" }
  /** Mirror a load state owned elsewhere, and delegate Retry to its owner. */
  | { readonly kind: "external"; readonly state: PresentationTemplatesLoadState; readonly onRetry: () => void };

export interface UsePresentationTemplatesOptions {
  /** Defaults to `{kind:"api"}` — the in-pane path. */
  readonly source?: PresentationTemplatesSource;
  /**
   * Overrides how a selection is committed, resolving with the number of
   * slides that landed in the deck. Defaults to inserting them here. Pass a
   * stable (memoised) callback.
   */
  readonly commitSelection?: (selectedIds: readonly string[]) => Promise<number>;
  /** Overrides how a slide card's real photo(s) are loaded. */
  readonly loadThumbnail?: (slide: PresentationTemplateSlide) => Promise<readonly ExtractedSlideImage[]>;
}

const API_SOURCE: PresentationTemplatesSource = { kind: "api" };

export function usePresentationTemplates({
  source = API_SOURCE,
  commitSelection,
}: UsePresentationTemplatesOptions = {}) {
  const http = useDeckorateHttp();
  const [fetchedState, setFetchedState] = useState<PresentationTemplatesLoadState>({ status: "loading" });
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [openPresentationId, setOpenPresentationId] = useState<string | null>(null);
  const [insertError, setInsertError] = useState<string | null>(null);
  const [insertStatus, setInsertStatus] = useState<string | null>(null);
  const [isInserting, setIsInserting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const isMountedRef = useRef(true);
  const isExternal = source.kind === "external";

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setFetchedState({ status: "loading" });
      try {
        const presentations = await fetchPresentationTemplates(http, signal ? { signal } : {});
        if (!isMountedRef.current) return;
        setFetchedState({ status: "ready", presentations });
      } catch (error) {
        if (isAbortError(error)) return;
        const message = getErrorMessage(error, FETCH_FAILED_MESSAGE);
        logPresentationTemplatesError("presentation-templates-fetch", message);
        if (!isMountedRef.current) return;
        setFetchedState({ status: "error", message });
      }
    },
    [http],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // An external source owns its own loading; fetching here would duplicate a
    // request this context can't even authorise.
    if (isExternal) return;
    const controller = new AbortController();
    load(controller.signal).catch((error: unknown) =>
      logPresentationTemplatesError("presentation-templates-fetch-unhandled", getErrorMessage(error, FETCH_FAILED_MESSAGE)),
    );
    return () => controller.abort();
  }, [isExternal, load]);

  const externalRetry = source.kind === "external" ? source.onRetry : null;
  const retry = useCallback(() => {
    if (externalRetry) {
      externalRetry();
      return;
    }
    load().catch((error: unknown) =>
      logPresentationTemplatesError("presentation-templates-retry", getErrorMessage(error, FETCH_FAILED_MESSAGE)),
    );
  }, [externalRetry, load]);

  const loadState = source.kind === "external" ? source.state : fetchedState;
  const presentations = loadState.status === "ready" ? loadState.presentations : [];

  const openPresentation = useCallback((id: string) => setOpenPresentationId(id), []);
  const closePresentation = useCallback(() => setOpenPresentationId(null), []);

  // A manifest refresh can drop the presentation currently open (or return a
  // different set entirely) — fall back to the grid rather than rendering a
  // preview for a presentation that no longer exists.
  useEffect(() => {
    if (openPresentationId === null) return;
    if (presentations.some((presentation) => presentation.id === openPresentationId)) return;
    setOpenPresentationId(null);
  }, [presentations, openPresentationId]);

  const openPresentationEntry = useMemo(
    () => presentations.find((presentation) => presentation.id === openPresentationId) ?? null,
    [presentations, openPresentationId],
  );

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSlides = useMemo(() => presentations.flatMap((presentation) => presentation.slides), [presentations]);

  // A presentation an administrator removes from the library while its slides
  // are selected is dropped from the selection quietly (BRD §10): once a
  // refreshed manifest no longer lists an id, it must not linger as a hidden pick.
  const loadedPresentations = loadState.status === "ready" ? loadState.presentations : null;
  useEffect(() => {
    if (!loadedPresentations) return;
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(loadedPresentations.flatMap((presentation) => presentation.slides.map((slide) => slide.id)));
      return new Set([...prev].filter((id) => present.has(id)));
    });
  }, [loadedPresentations]);

  // Only slides still present in the current manifest count as selected — a
  // retry that returns a different set must not leave phantom ids selected.
  const selectedSlides = useMemo(
    () => allSlides.filter((slide) => selectedIds.has(slide.id)),
    [allSlides, selectedIds],
  );
  const insertableSelected = useMemo(() => selectedSlides.filter((slide) => slide.insertable), [selectedSlides]);

  /** Commits `ids` (callers guarantee a non-empty list and no insert in flight).
   *  Returns whether it succeeded, so a failure leaves the selection for a retry. */
  const commitIds = useCallback(
    async (ids: readonly string[]): Promise<boolean> => {
      setIsInserting(true);
      setInsertError(null);
      setInsertStatus(null);
      try {
        // The dialog flow checks this in the pane (see useAssetLibraryLauncher);
        // here the panel is the pane, so it checks for itself.
        if (!commitSelection && isPresentationDeckReadOnly()) throw new PresentationDeckReadOnlyError();
        const inserted = commitSelection ? await commitSelection(ids) : await insertPresentationTemplates(allSlides, ids);
        if (isMountedRef.current) {
          setInsertStatus(presentationSlidesAddedMessage(inserted, sourcePresentationNames(presentations, ids)));
        }
        return true;
      } catch (error) {
        const kind = classifyInsertFailure(error);
        const message = kind ? PRESENTATION_INSERT_NOTICE_COPY[kind].message : getErrorMessage(error, INSERT_FAILED_MESSAGE);
        logPresentationTemplatesError("presentation-templates-insert", message);
        if (isMountedRef.current) {
          setInsertError(kind ? `${PRESENTATION_INSERT_NOTICE_COPY[kind].title} ${message}` : message);
        }
        return false;
      } finally {
        if (isMountedRef.current) setIsInserting(false);
      }
    },
    [allSlides, presentations, commitSelection],
  );

  const insertSelected = useCallback(async () => {
    if (isInserting || selectedSlides.length === 0) return;
    if (insertableSelected.length === 0) {
      setInsertError(NO_SLIDE_DATA_MESSAGE);
      return;
    }
    const succeeded = await commitIds(insertableSelected.map((slide) => slide.id));
    if (succeeded && isMountedRef.current) setSelectedIds(new Set());
  }, [isInserting, selectedSlides, insertableSelected, commitIds]);

  /** Selects every slide of `presentationId` (BRD 8.6 "Select all"). Other
   *  presentations' picks are left alone — selection is flat across them. */
  const selectAllSlides = useCallback(
    (presentationId: string) => {
      const presentation = presentations.find((candidate) => candidate.id === presentationId);
      if (!presentation) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const slide of presentation.slides) next.add(slide.id);
        return next;
      });
    },
    [presentations],
  );

  /** Deselects every slide of `presentationId` (BRD 8.6 "Deselect all"). */
  const deselectAllSlides = useCallback(
    (presentationId: string) => {
      const presentation = presentations.find((candidate) => candidate.id === presentationId);
      if (!presentation) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const slide of presentation.slides) next.delete(slide.id);
        return next;
      });
    },
    [presentations],
  );

  /** Drops every pick, across all presentations (the footer's "Clear All"). */
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  /** Shared by the card's whole-deck download and the preview's Download
   *  Pack: `slideIds` undefined means the whole deck. Never touches the
   *  slide selection. */
  const runDownload = useCallback(
    async (presentationId: string, slideIds?: readonly string[]) => {
      if (isDownloading) return;
      const presentation = presentations.find((candidate) => candidate.id === presentationId);
      if (!presentation) return;
      setIsDownloading(true);
      setInsertError(null);
      setInsertStatus(null);
      try {
        await downloadPresentationPack(presentation, slideIds);
      } catch (error) {
        const message = getErrorMessage(error, DOWNLOAD_FAILED_MESSAGE);
        logPresentationTemplatesError("presentation-templates-download", message);
        if (isMountedRef.current) setInsertError(message);
      } finally {
        if (isMountedRef.current) setIsDownloading(false);
      }
    },
    [isDownloading, presentations],
  );

  /** Saves the whole presentation as a standalone .pptx (BRD 8.4), without
   *  opening it. */
  const downloadPresentation = useCallback((presentationId: string) => runDownload(presentationId), [runDownload]);

  /**
   * The preview's "Download Pack" (BRD 8.7): exactly the slides picked in this
   * presentation, or the whole deck when none — or every one — is picked
   * (all of them is the whole deck anyway, so the source file goes out as-is).
   */
  const downloadPack = useCallback(
    async (presentationId: string) => {
      const presentation = presentations.find((candidate) => candidate.id === presentationId);
      if (!presentation) return;
      const picked = presentation.slides.filter((slide) => selectedIds.has(slide.id)).map((slide) => slide.id);
      const isPartial = picked.length > 0 && picked.length < presentation.slides.length;
      await runDownload(presentationId, isPartial ? picked : undefined);
    },
    [presentations, selectedIds, runDownload],
  );

  return {
    loadState,
    insertStatus,
    presentations,
    openPresentationId,
    /** The currently-open presentation's own record, or null in grid view. */
    activePresentation: openPresentationEntry,
    openPresentation,
    closePresentation,
    selectedIds,
    selectedCount: selectedSlides.length,
    toggleSelected,
    retry,
    insertSelected,
    selectAllSlides,
    deselectAllSlides,
    downloadPresentation,
    downloadPack,
    clearSelection,
    isDownloading,
    isInserting,
    insertError,
  };
}
